import React, { useContext } from 'react';
import { isMobileOnly } from 'react-device-detect';
import {
  BAR_TYPE,
  CONFIG_ADMIN_USER_TYPE,
  CONFIG_ADMIN_VIEW_USER_TYPE,
  ENERGY_PERFORMANCE_WIDGET,
  ENERGY_PRODUCTION_WIDGET,
  ENERGY_RANKING_WIDGET,
  ENERGY_REWARDS_WIDGET,
  energyWidgetConf,
  eurWidgetConf,
  KWH,
  latestUpdateLabelKpi,
  MEMBER_USER_TYPE,
  MEMBER_VIEW_USER_TYPE,
  MOMENT_DAY,
  MONTH,
  NO_DATA,
  percentageWidgetConf,
  percentageWidgetConfLeft,
  RP_CONFIGURATION,
  RP_DATE,
  RP_EC_ID,
  RP_POD,
  SQUARE_LINE_CHART,
  timeFormatMapper,
} from 'consts';
import { Context } from 'context/Context';
import { EnelXGuiAtoms, EnelXGuiComponents } from 'gui';
import useFormatter from 'hook/useFormatter';
import useValidator from 'hook/useValidator';
import moment from 'moment';
import { useTheme } from 'styled-components';
import {
  dataTypeOptions,
  defaultMaxDate, getDefaultTimeOptionsMapper, granularityOptions, mapOptions, SelectGranularityWrapper,
} from 'utils';
import {
  customRankingPlugin,
  dashedBorderPlugin, getBarColor, segmentStyle, titlePluginPerformance, titlePluginRewards,
} from 'utils/widgetsUtils';

const {
  MigrationSelect, MigrationIcon,
} = EnelXGuiAtoms;

const {
  MigrationTooltip,
} = EnelXGuiComponents;

export const useFilterHook = ({
  energyCommunities,
  selectedEc,
  configSelected,
  podSelected,
  t,
  epoch,
  currentTimeZone,
  setFilters,
  filters,
  currentTemplate,
  setCurrentTemplate,
}) => {
  const selectedEcObj = Array.isArray(energyCommunities) ? energyCommunities.find((ec) => ec.ecId === selectedEc) : undefined;
  const configList = selectedEcObj ? selectedEcObj.configurations : [];

  const configOptions = mapOptions(configList);

  const selectedConfigObj = configList.find((configObj) => configObj.ecId === configSelected);
  const podList = selectedConfigObj ? selectedConfigObj.loadAreas : [];

  const podOptions = mapOptions(podList);

  const dateFilter = {
    id: RP_DATE,
    type: 'date',
    label: t('filters.timePeriod'),
    maxDate: defaultMaxDate(epoch),
    defaultValue: 'currentMonth',
    disableChipClose: true,
    dateOptions: ['currentMonth', 'last3months', 'past6months'],
    dateValuesMapper: getDefaultTimeOptionsMapper(currentTimeZone),
    width: '215px',
  };

  const memberArr = [
    {
      id: RP_CONFIGURATION,
      type: 'select',
      label: t('filters.configuration'),
      options: configOptions,
      disableChipClose: true,
      onSelect: (value) => {
        const podValue = energyCommunities.find(
          (ec) => ec.ecId === selectedEc,
        )?.configurations?.find((config) => config.ecId === value)?.loadAreas[0]?.ecId;
        setFilters({
          ...filters,
          configurationFilter: value,
          podFilter: podValue,
        });
        currentTemplate.searchbar.filtersValue = [
          {
            id: RP_EC_ID,
            value: selectedEc,
          },
          {
            id: RP_CONFIGURATION,
            value,
          },
          {
            id: RP_POD,
            value: podValue,
          },
        ];
        setCurrentTemplate(currentTemplate);
      },
      defaultValue: configSelected,
    },
    {
      id: RP_POD,
      type: 'select',
      label: t('filters.loadArea'),
      options: podOptions,
      disableChipClose: true,
      defaultValue: podSelected,
    },
    dateFilter,
  ];

  return {
    selectedEcObj,
    configList,
    configOptions,
    selectedConfigObj,
    podList,
    podOptions,
    dateFilter,
    memberArr,
  };
};

export const useWidgetHook = ({
  t, widgetsData,
  theme, isLoading, countryID, granularity,
  setGranularity, dataChartType, setDataChartType, userType, podValue, isDashboard, isDateDiffUnder30Days,
}) => {
  const {
    unit: { formatElectricEnergy }, currency: { formatCurrency }, epoch: { currentTimeZone },
  } = useFormatter();

  const { isValidNumber } = useValidator();

  const {
    localization: {
      countryID: locale,
    },
  } = useContext(Context);

  const {
    revenueData,
    energyReportData,
    energyPerformanceData,
    rankingData,
    saturation,
    generation,
    consumption,
    shared,
    contemporaneity,
  } = widgetsData ?? {};

  const initObj = {
    intervalConsumption: [],
    intervalGeneration: [],
    intervalSharedEnergy: [],
    intervalSharedGeneratedRatio: [],
    intervalContemporaneity: [],
    powerData: [],
    energyLabels: [],
  };

  const monthLabel = isMobileOnly ? 'MMM YYYY' : 'MMMM YYYY';

  const {
    intervalConsumption,
    intervalGeneration,
    intervalSharedEnergy,
    intervalSharedGeneratedRatio,
    intervalContemporaneity,
    energyLabels,
  } = Array.isArray(energyReportData) ? energyReportData.reduce((acc, curr) => {
    if (curr.intervalDataOrigin === dataChartType || dataChartType === 'all') {
      acc.energyLabels.push(moment.tz(curr.startTime * 1000, currentTimeZone)
        .locale(locale).format(monthLabel));
      acc.intervalConsumption.push({
        x: moment.tz(curr.startTime * 1000, currentTimeZone),
        y: curr.intervalConsumption,
        intervalDataOrigin: curr.intervalDataOrigin,
      });
      acc.intervalGeneration.push({
        x: moment.tz(curr.startTime * 1000, currentTimeZone),
        y: curr.intervalGeneration,
        intervalDataOrigin: curr.intervalDataOrigin,
      });
      acc.intervalSharedEnergy.push({
        x: moment.tz(curr.startTime * 1000, currentTimeZone),
        y: curr.intervalSharedEnergy,
        intervalDataOrigin: curr.intervalDataOrigin,
      });
      acc.intervalSharedGeneratedRatio.push({
        x: moment.tz(curr.startTime * 1000, currentTimeZone),
        y: curr.intervalSaturation,
        intervalDataOrigin: curr.intervalDataOrigin,
      });
      acc.intervalContemporaneity.push({
        x: moment.tz(curr.startTime * 1000, currentTimeZone),
        y: curr.contemporaneityFactor,
        intervalDataOrigin: curr.intervalDataOrigin,
      });
    }

    return acc;
  }, initObj) : initObj;

  const energyCommunityPerformanceWidgets = [
    {
      id: 'contemporaneityFactor',
      label: t('widgets.contemporaneityFactor'),
      data: [energyPerformanceData?.memberContemporaneityFactor],
      color: theme.colors.primary.midBlue,
      total: `${energyPerformanceData?.memberContemporaneityFactor?.toFixed(2).replace('.', ',') ?? NO_DATA} %`,
      labelTitle: t('widgets.total'),
    },
    {
      id: 'contemporanrityRatio',
      label: t('widgets.contemporanrityRatio'),
      data: [energyPerformanceData?.clusterContemporaneityFactor],
      color: theme.colors.primary.tanLight,
      total: `${energyPerformanceData?.clusterContemporaneityFactor?.toFixed(2).replace('.', ',') ?? NO_DATA} %`,
      labelTitle: t('widgets.total'),
    },
  ];

  const initObj2 = {
    chartRevenueLabels: [],
    energyCommunityRewards: [],
  };

  const {
    chartRevenueLabels,
    energyCommunityRewards,
  } = Array.isArray(revenueData) ? revenueData.reduce((acc, curr) => {
    acc.chartRevenueLabels.push(moment(curr.monthDate).locale(locale).format(monthLabel));
    acc.energyCommunityRewards.push(curr.revenue?.toFixed(2));
    return acc;
  }, initObj2) : initObj2;

  const { memberRankingData } = Array.isArray(rankingData) ? rankingData.reduce((acc, curr) => {
    acc.memberRankingData.push({
      x: curr.date,
      y: curr.contRatio,
    });
    return acc;
  }, {
    memberRankingData: [],
  }) : { memberRankingData: [] };

  const energyCommunityRewardsWidgets = [
    {
      id: 'energyCommunityRewards',
      label: t('widgets.rewardsTitle'),
      data: [...energyCommunityRewards],
      color: theme.colors.primary.tanLight,
      total: formatCurrency(widgetsData?.rewards, 0),
      labelTitle: t('widgets.total'),
    },
  ];

  const memberRankingWidgets = [
    {
      id: 'memberRanking',
      label: t('widgets.yourRanking'),
      data: [...memberRankingData],
      color: theme.colors.accent.primary,
      labelTitle: t('widgets.total'),
      total: '',
    },
  ];

  const yellowValueDashboard = {
    label: t('widgets.intervalSharedGeneratedRatio'),
    data: intervalSharedGeneratedRatio,
    total: `${isValidNumber(saturation) ? saturation.toFixed(0) : '-'} %`,
  };

  const yellowValueMyData = (userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE
  || userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE) && podValue !== 'all' ? {
      label: t('widgets.contemporaneity'),
      data: intervalContemporaneity,
      total: `${isValidNumber(contemporaneity) ? contemporaneity.toFixed(0) : '-'} %`,
    } : {
      label: t('widgets.intervalSharedGeneratedRatio'),
      data: intervalSharedGeneratedRatio,
      total: `${isValidNumber(saturation) ? saturation.toFixed(0) : '-'} %`,
    };

  const yellowValueObj = isDashboard ? yellowValueDashboard : yellowValueMyData;

  const firstMonth = energyLabels[0];
  const lastMonth = energyLabels[energyLabels.length - 1];

  const performanceLabel = firstMonth === lastMonth ? `${lastMonth}` : `${firstMonth} - ${lastMonth}`;

  const solarColorMap = {
    BAD: '210, 25, 49',
    MEDIUM: '251, 211, 132',
    GOOD: '2, 119, 166',
    EXCELLENT: '27, 125, 97',
  };

  const solarIconMap = {
    BAD: 'sentiment_dissatisfied',
    MEDIUM: 'sentiment_neutral',
    GOOD: 'sentiment_satisfied',
    EXCELLENT: 'sentiment_very_satisfied',
  };

  const solarColorValue = energyPerformanceData?.solarHappiness
    ? solarColorMap[energyPerformanceData.solarHappiness] : undefined;

  const solarIconValue = energyPerformanceData?.solarHappiness
    ? solarIconMap[energyPerformanceData.solarHappiness] : undefined;

  const performanceInfoLabel = {
    BAD: t('kpi.badPerformanceInfo'),
    MEDIUM: t('kpi.mediumPerformanceInfo'),
    GOOD: t('kpi.goodPerformanceInfo'),
    EXCELLENT: t('kpi.bestPerformanceInfo'),
  };

  const energyCommunityReportWidgets = [
    {
      id: 'communityDispatched',
      label: t('widgets.communityDispatched'),
      data: intervalGeneration,
      color: granularity === MONTH
        ? getBarColor(intervalGeneration, '2, 119, 166') : theme.colors.primary.midBlue,
      labelTitle: t('widgets.communityDispatched'),
      total: formatElectricEnergy(generation, KWH, 0),
      yAxisID: 'y',
      segment: segmentStyle,
      borderColor: theme.colors.primary.midBlue,
    },
    {
      id: 'communityEnergyConsumption',
      label: `${t('widgets.communityEnergyConsumption')}`,
      data: intervalConsumption,
      color: granularity === MONTH
        ? getBarColor(intervalGeneration, '210, 25, 49') : theme.colors.primary.midRed,
      labelTitle: `${t('widgets.communityEnergyConsumption')}`,
      total: formatElectricEnergy(consumption, KWH, 0),
      yAxisID: 'y',
      segment: segmentStyle,
      borderColor: theme.colors.primary.midRed,
    },
    {
      id: 'communityShared',
      label: t('widgets.communityShared'),
      data: intervalSharedEnergy,
      color: granularity === MONTH
        ? getBarColor(intervalGeneration, '27, 125, 97') : theme.colors.primary.midGreen,
      labelTitle: t('widgets.communityShared'),
      total: formatElectricEnergy(shared, KWH, 0),
      yAxisID: 'y',
      segment: segmentStyle,
      borderColor: theme.colors.primary.midGreen,
      fill: true,
    },
    {
      id: 'intervalSharedGeneratedRatio',
      label: yellowValueObj.label,
      data: yellowValueObj.data,
      color: granularity === MONTH
        ? getBarColor(intervalGeneration, '251, 211, 132') : theme.colors.primary.yellowOrange,
      labelTitle: yellowValueObj.label,
      total: yellowValueObj.total,
      yAxisID: '%',
      segment: segmentStyle,
      borderColor: theme.colors.primary.yellowOrange,
    },
  ];

  const customTooltipLabelMapper = (value) => ({
    communityDispatched: formatElectricEnergy(value, KWH, 2),
    communityEnergyConsumption: formatElectricEnergy(value, KWH, 2),
    communityShared: formatElectricEnergy(value, KWH, 2),
    intervalSharedGeneratedRatio: `${Number(value).toFixed(2).replace('.', ',')} %`,
  });

  const lineFooter = (
    <div className="flex items-center gap-5 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="w-8 h-4 bg-primary-black flex items-center justify-center" />
        <span>{`${t('widgets.data')} ${t('widgets.certified')}`}</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-8 h-1 bg-primary-black flex items-center justify-center" />
        <span>{`${t('widgets.data')} ${t('widgets.measured')}`}</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-8 h-1 border-dashed border-status-black border-2 flex items-center justify-center" />
        <span>{`${t('widgets.data')} ${t('widgets.estimated')}`}</span>
      </div>
    </div>
  );

  const barFooter = (
    <div className="flex items-center gap-5 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="w-6 h-6 bg-primary-black flex items-center justify-center" />
        <span>{`${t('widgets.data')} ${t('widgets.certified')}`}</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative w-6 h-6 border-2 border-primary-black">
          <div className="absolute inset-0 flex justify-center items-center">
            <div className="transform -rotate-45 w-6 h-1 bg-primary-black" />
          </div>
        </div>
        <span>{`${t('widgets.data')} ${t('widgets.measured')}`}</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-6 h-6 border-dashed border-status-black border-2 flex items-center justify-center" />
        <span>{`${t('widgets.data')} ${t('widgets.estimated')}`}</span>
      </div>
    </div>
  );

  const rewardsWidget = {
    id: ENERGY_REWARDS_WIDGET,
    title: t('widgets.rewardsTitle'),
    titleTooltip: (
      <ul className="list-disc">
        <li className="text-primary-tanLight text-2xl ml-3">
          <span className="text-primary-black text-sm">{`${t('widgets.rewardsTitle')}: `}</span>
          <span className="text-primary-black text-sm font-normal">{t('widgets.rewardsTotal')}</span>
        </li>
      </ul>
    ),
    customLegend: true,
    customPlugin: titlePluginRewards,
    type: BAR_TYPE,
    labels: chartRevenueLabels,
    country: countryID,
    customTooltipLabel: (value) => `${value?.dataset?.label}: ${formatCurrency(Number(value?.parsed?.y ?? NO_DATA), 0)}`,
    dataset: energyCommunityRewardsWidgets,
    isLoading,
    widgetMinimized: energyCommunityRewardsWidgets?.map((e) => `${e.label}: ${e.total} ${t('widgets.total')}`),
    axisConf: [eurWidgetConf],
  };

  const energyProductionWidget = {
    id: ENERGY_PRODUCTION_WIDGET,
    title:
    isDashboard && (userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE)
      ? t('widgets.reportUserTitle') : t('widgets.reportAdminTitle'),
    titleTooltip: (
      <ul className="list-disc">
        <li className="text-primary-midBlue text-2xl ml-3">
          <span className="text-primary-black text-sm">{`${t('widgets.communityDispatched')}: `}</span>
          <span className="text-primary-black text-sm font-normal">{t('widgets.communityDispatchedTooltip')}</span>
        </li>
        <li className="text-primary-midRed text-2xl ml-3">
          <span className="text-primary-black text-sm">{`${t('widgets.communityEnergyConsumption')}: `}</span>
          <span className="text-primary-black text-sm font-normal">{t('widgets.communityConsumptionTooltip')}</span>
        </li>
        <li className="text-primary-midGreen text-2xl ml-3">
          <span className="text-primary-black text-sm">{`${t('widgets.intervalSharedGeneratedRatio')}(kWh): `}</span>
          <span className="text-primary-black text-sm font-normal">{t('widgets.communitySharedTooltipKwh')}</span>
        </li>
        <li className="text-primary-yellowOrange text-2xl ml-3">
          <span className="text-primary-black text-sm">{`${t('widgets.intervalSharedGeneratedRatio')}(%): `}</span>
          <span className="text-primary-black text-sm font-normal">{t('widgets.communitySharedTooltipPercentage')}</span>
        </li>
      </ul>
    ),
    customLegend: true,
    customPlugin: dashedBorderPlugin,
    customComponent: (
      <SelectGranularityWrapper>
        <div>
          <div style={{ width: '150px' }}>
            <MigrationSelect
              label={t('widgets.dataType')}
              options={dataTypeOptions(t)}
              value={dataChartType}
              onChange={(_label, value) => setDataChartType(value)}
              returnLabelValue
            />
          </div>
        </div>
        <div>
          <div style={{ width: '150px' }}>
            <MigrationSelect
              label={t('widgets.granularity')}
              options={granularityOptions(t, isDateDiffUnder30Days)}
              value={granularity ?? MONTH}
              onChange={(_label, value) => setGranularity(value)}
              returnLabelValue
            />
          </div>
        </div>
      </SelectGranularityWrapper>
    ),
    type: granularity === MONTH ? BAR_TYPE : SQUARE_LINE_CHART,
    timeFormat: timeFormatMapper[granularity],
    timeUnit: granularity,
    labels: energyLabels,
    country: countryID,
    dataset: energyCommunityReportWidgets,
    customTooltipLabel: (value) => (
      `${value?.dataset?.label}: ${customTooltipLabelMapper(value?.parsed?.y)[value?.dataset?.id]}`
    ),
    isLoading,
    widgetMinimized: energyCommunityReportWidgets?.map((e) => `${e.label}: ${e.total} ${t('widgets.total')}`),
    axisConf: [energyWidgetConf, percentageWidgetConf],
    footer: granularity === MONTH ? barFooter : lineFooter,
  };

  const energyRanking = {
    id: ENERGY_RANKING_WIDGET,
    title: t('widgets.rankingTitle'),
    titleTooltip: (
      <ul className="list-disc">
        <li className="text-primary-tanLight text-2xl ml-3">
          <span className="text-primary-black text-sm">{`${t('widgets.rankingTitle')}: `}</span>
          <span className="text-primary-black text-sm font-normal">{t('widgets.rankingTooltip')}</span>
        </li>
      </ul>
    ),
    customLegend: true,
    customPlugin: customRankingPlugin,
    type: SQUARE_LINE_CHART,
    timeFormat: timeFormatMapper.day,
    timeUnit: MOMENT_DAY,
    country: countryID,
    customTooltipLabel: (value) => `${value?.dataset?.label}: ${value?.parsed?.y?.toFixed(2).replace('.', ',')}`,
    dataset: memberRankingWidgets,
    isLoading,
    footer: (
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-primary-midRed flex items-center justify-center" />
            <span className={isMobileOnly ? 'text-sm' : ''}>{t('widgets.lowRanking')}</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-primary-yellowOrange flex items-center justify-center" />
            <span className={isMobileOnly ? 'text-sm' : ''}>{t('widgets.mediumRanking')}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-primary-midBlue flex items-center justify-center" />
            <span className={isMobileOnly ? 'text-sm' : ''}>{t('widgets.goodRanking')}</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-primary-midGreen flex items-center justify-center" />
            <span className={isMobileOnly ? 'text-sm' : ''}>{t('widgets.bestRanking')}</span>
          </div>
        </div>
      </div>
    ),
  };

  const energyPerformanceWidget = {
    id: ENERGY_PERFORMANCE_WIDGET,
    title: t('widgets.performanceTitle'),
    titleTooltip: (
      <ul className="list-disc">
        <li className="text-primary-tanLight text-2xl ml-3">
          <span className="text-primary-black text-sm">{`${t('widgets.performanceTitle')}: `}</span>
          <span className="text-primary-black text-sm font-normal">{t('widgets.performanceWidget')}</span>
        </li>
      </ul>
    ),
    subtitle: (
      <div
        className="flex items-center gap-3 border-2 rounded-sm p-1"
        style={{
          borderColor: `rgba(${solarColorValue})`,
          backgroundColor: `rgba(${solarColorValue}, 0.1)`,
        }}
      >
        <MigrationIcon
          library="material"
          name={solarIconValue}
          size="35px"
          color={`rgba(${solarColorValue})`}
        />
        <div className="flex-col">
          <p className="font-bold">
            {energyPerformanceData?.solarHappiness ? t(`kpi.${energyPerformanceData.solarHappiness}`) : NO_DATA}
          </p>
          <p>
            {energyPerformanceData?.solarHappiness ? performanceInfoLabel[energyPerformanceData?.solarHappiness] : NO_DATA}
          </p>
        </div>
      </div>
    ),
    customLegend: true,
    customPlugin: titlePluginPerformance,
    type: BAR_TYPE,
    labels: [performanceLabel],
    country: countryID,
    customTooltipLabel: (value) => `${value?.dataset?.label}: ${value?.parsed?.y?.toFixed(2).replace('.', ',')} %`,
    dataset: energyCommunityPerformanceWidgets,
    isLoading,
    widgetMinimized: energyCommunityPerformanceWidgets?.map((e) => `${e.label}: ${e.total} ${t('widgets.total')}`),
    axisConf: [percentageWidgetConfLeft],
  };

  return {
    rewardsWidget,
    energyProductionWidget,
    energyRanking,
    energyPerformanceWidget,
  };
};

export const useKpiHook = ({
  t, processedData, isWidgetAvailable, countryID, isFetchingRevenue, resource,
}) => {
  const loading = t('widgets.loading');
  const theme = useTheme();

  const greyColor = theme.colors.custom.backgroundGrey;

  const healthColorMap = {
    LOW: theme.colors.primary.midRed,
    MEDIUM: theme.colors.primary.orange,
    HIGH: theme.colors.primary.midBlue,
    EXCELLENT: theme.colors.primary.midGreen,
  };

  const healthIconMap = {
    LOW: 'sentiment_dissatisfied',
    MEDIUM: 'sentiment_neutral',
    HIGH: 'sentiment_satisfied',
    EXCELLENT: 'sentiment_very_satisfied',
  };

  const healthColorValue = processedData?.health ? healthColorMap[processedData?.health] : undefined;
  const healthIconValue = processedData?.health ? healthIconMap[processedData?.health] : undefined;

  const updateDataKpi = {
    callbacks: [],
    id: 'lastMeasuredUpdateKpi',
  };

  [
    {
      id: 'lastMeasuredUpdateKpi',
      text: t('kpi.dataMeasured'),
      value: processedData?.lastMeasuredUpdate,
    }, {
      id: 'lastEstimatedUpdateKpi',
      text: t('kpi.dataStimated'),
      value: processedData?.lastEstimatedUpdate,
    }, {
      id: 'lastFiscalUpdateKpi',
      text: t('kpi.dataFiscal'),
      value: processedData?.lastFiscalUpdate,
    },
  ].forEach((kpi) => {
    if (isWidgetAvailable && isWidgetAvailable(resource, kpi.id)) {
      const labelValue = kpi.value ? latestUpdateLabelKpi(kpi.value, countryID) : t('kpi.noData');
      updateDataKpi.callbacks.push(() => ({
        slotId: kpi.id,
        topElement: (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-5">
              <MigrationIcon
                name="clock"
                size="25px"
                color={greyColor}
              />
              <p>{kpi.text}</p>
            </div>
            <MigrationTooltip
              text={t('kpi.dataTooltip')}
            />
          </div>
        ),
        body: {
          description: t('kpi.lastUpdate'),
          title: isFetchingRevenue ? loading : labelValue,
        },
      }));
    }
  });

  const totalMembersKpi = (title, tooltip) => ({
    callbacks: [
      () => ({
        slotId: 'totalMembersKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="user-icon"
              size="28px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description: t('kpi.membersTitle'),
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'totalMembersKpi',
    title: t('kpi.membersTitle'),
  });

  const totalSharedEnergyKpi = (title, tooltip, description) => ({
    callbacks: [
      () => ({
        slotId: 'totalSharedEnergyKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="electricity-bolt"
              size="30px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description,
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'totalSharedEnergyKpi',
    title: t('kpi.totalSharedEnergy'),
  });

  const totalPowerKpi = (title, tooltip) => ({
    callbacks: [
      () => ({
        slotId: 'totalPowerKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="solar-panel-2"
              size="25px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description: t('kpi.totalPower'),
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'totalPowerKpi',
    title: t('kpi.totalPower'),
  });

  const revenueKpi = (title, tooltip, description) => ({
    callbacks: [
      () => ({
        slotId: 'totalRevenueKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="piggy-bank"
              size="30px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description,
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'totalRevenueKpi',
    title: t('kpi.totalRevenue'),
  });

  const saturationKpi = (title, tooltip) => ({
    callbacks: [
      () => ({
        slotId: 'saturationKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-5">
              <MigrationIcon
                name="people"
                size="35px"
                color={greyColor}
              />
              <div className="flex items-center gap-1 border-2 rounded-sm p-1" style={{ borderColor: healthColorValue }}>
                <MigrationIcon
                  library="material"
                  name={healthIconValue}
                  size="35px"
                  color={healthColorValue}
                />
                <p>{processedData?.health ? t(`kpi.${processedData?.health}`) : NO_DATA}</p>
              </div>
            </div>
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description: t('kpi.saturation'),
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'saturationKpi',
    title: t('kpi.saturation'),
  });

  const totalConfigurationsKpi = (title, tooltip) => ({
    callbacks: [
      () => ({
        slotId: 'totalConfigurationsKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="settings"
              size="20px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description: t('kpi.config'),
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'totalConfigurationsKpi',
    title: t('kpi.config'),
  });

  const savedCo2Kpi = (title, tooltip) => ({
    callbacks: [
      () => ({
        slotId: 'savedCO2Kpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="leaf"
              size="25px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description: t('kpi.co2'),
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'savedCO2Kpi',
    title: t('kpi.co2'),
  });

  const totalPlantsKpi = (title, tooltip) => ({
    callbacks: [
      () => ({
        slotId: 'totalPlantsKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="solar-panel-2"
              size="25px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description: t('kpi.totalPlants'),
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'totalPlantsKpi',
    title: t('kpi.totalPlants'),
  });

  const totalConsumptioKpi = (title, tooltip) => ({
    callbacks: [
      () => ({
        slotId: 'totalConsumptionKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="electric-meter"
              size="30px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description: t('kpi.totalMemberConsumption'),
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'totalConsumptionKpi',
    title: t('kpi.totalMemberConsumption'),
  });

  const totalGenerationKpi = (title, tooltip) => ({
    callbacks: [
      () => ({
        slotId: 'totalGenerationKpi',
        topElement: (
          <div className="flex justify-between items-center">
            <MigrationIcon
              name="electricity-bolt"
              size="30px"
              color={greyColor}
            />
            <MigrationTooltip
              text={tooltip}
            />
          </div>
        ),
        body: {
          description: t('kpi.totalGeneration'),
          title: isFetchingRevenue ? loading : title,
        },
      }),
    ],
    id: 'totalGenerationKpi',
    title: t('kpi.totalGeneration'),
  });

  return {
    updateDataKpi,
    totalMembersKpi,
    totalSharedEnergyKpi,
    totalPowerKpi,
    revenueKpi,
    saturationKpi,
    totalConfigurationsKpi,
    savedCo2Kpi,
    totalPlantsKpi,
    totalConsumptioKpi,
    totalGenerationKpi,
  };
};
